import { WidgetType } from 'domain/entity/widget';
import WidgetData, { Color, WidgetDataAvailability, WidgetDescription } from 'domain/entity/widgetData';

export enum Resolution {
    B = 'B',
    FM = 'FM',
    TM = 'TM',
    QH = 'QH',
    HH = 'HH',
    H = 'H',
    D = 'D',
    W = 'W',
    M = 'M',
    Y = 'Y',
}

export enum ChartAxisType {
    DATETIME = 'DATETIME',
    DATE = 'DATE',
    NUMERIC = 'NUMERIC',
    DAY_HOURS = 'DAY_HOURS',
    WEEK_DAYS = 'WEEK_DAYS',
    CATEGORIZED = 'CATEGORIZED',
}

export type ChartValue = DateChartValue | NumericChartValue;
export type DateChartValue = { x: Date; y: number | null; group?: string; unmodifiedX?: Date };
export type NumericChartValue = { x: number; y: number | null; t?: Date; group?: string; unmodifiedX?: Date };

export type ChartAxisMeta = CategorizedChartAxisMeta | DatetimeChartAxisMeta | Record<string, never>;

export type CategorizedChartAxisMeta = {
    categories: string[];
};
export type DatetimeChartAxisMeta = {
    timezone: string;
    resolution: Resolution;
    from: Date;
    to: Date;
};

type CommonChartAxisProps = {
    key: string;
    title: string;
    units: string;
};
export type ChartAxis = DateTimeChartAxis | CategorizedChartAxis | NumericChartAxis;

export type DateTimeChartAxis = CommonChartAxisProps & {
    type: ChartAxisType.DATETIME;
    meta: DatetimeChartAxisMeta | Record<string, never>;
};

export type CategorizedChartAxis = CommonChartAxisProps & {
    type: ChartAxisType.CATEGORIZED;
    meta: CategorizedChartAxisMeta;
};

export type NumericChartAxis = CommonChartAxisProps & {
    type: ChartAxisType.DAY_HOURS | ChartAxisType.NUMERIC;
    meta: Record<string, never>;
};

export type ChartWidgetData = WidgetData & {
    series: ChartSerie[];
    axesX: ChartAxis[];
    axesY: ChartAxis[];
};

export enum SeriesType {
    LINE = 'LINE',
    BAR = 'BAR',
    STACKED_BAR = 'STACKED_BAR',
    GROUPED_BAR = 'GROUPED_BAR',
    GROUPED_LINE = 'GROUPED_LINE',
    TOOLTIP = 'TOOLTIP',
    SCATTER = 'SCATTER',
}

export enum ChartSeriesType {
    LINE = 'line',
    AREA = 'area',
    COLUMN = 'column',
    GROUPED_BAR = 'grouped_bar',
    GROUPED_LINE = 'grouped_line',
    TOOLTIP = 'tooltip',
    CHART_LIMIT = 'chart_limit',
    SCATTER = 'scatter',
}

export enum SerieBorder {
    DASHED = 'DASHED',
    DOTTED = 'DOTTED',
    SOLID = 'SOLID',
}

export enum ChartSerieMetaBorder {
    DASH = 'dash',
    DOT = 'dot',
    SOLID = 'solid',
}

export type VoidSeriesMeta = Record<string, never>;

export type ChartSerieMeta = {
    groups?: { key: string; name: string; color: string }[];
    color?: string;
    area?: boolean;
    step?: SerieMetaStep;
    border?: ChartSerieMetaBorder;
};

export type ChartSerie = {
    type: ChartSeriesType;
    values: ChartValue[];
    color: Color;
    axisY: string;
    axisX: string;
    title: string;
    meta: ChartSerieMeta;
    stack: string | null;
    fillPattern: SerieBorder;
    pointLess: boolean;
    resolution: Resolution;
};

export enum SerieMetaStep {
    CENTER = 'CENTER',
    RIGHT = 'RIGHT',
    LEFT = 'LEFT',
    NONE = 'NONE',
}

class ChartData implements ChartWidgetData {
    constructor(
        readonly type: WidgetType,
        public series: ChartSerie[],
        readonly axesX: ChartAxis[],
        readonly axesY: ChartAxis[],
        readonly dataAvailability: WidgetDataAvailability,
        readonly description: WidgetDescription
    ) {}
}

export default ChartData;
